import type { ReactElement } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button, cn } from '@xdocs/ui';
import { useEditorT } from './useEditorT.js';
import styles from './ConflictBanner.module.css';

export interface ConflictBannerProps {
  /** Required — names the asset (§16.1), never "this document". */
  documentTitle: string;
  onReload: () => void;
  isReloading?: boolean;
  className?: string | undefined;
}

/**
 * F67 "conflict" state: a concurrent editor/owner save landed past this
 * tab's `baseVersionId`. Page-level banner atop the affected region
 * (ux.md), never a silent clobber — freezes further autosave until the
 * user explicitly reloads (`useEditorAutosave`'s `resetContent`).
 */
export function ConflictBanner({ documentTitle, onReload, isReloading = false, className }: ConflictBannerProps): ReactElement {
  const { t } = useEditorT();

  return (
    <div role="alert" className={cn(styles['banner'], className)}>
      <AlertTriangle size={20} className={styles['icon']} aria-hidden="true" />
      <div className={styles['body']}>
        <p className={styles['title']}>{t('editor:conflict.title')}</p>
        <p className={styles['description']}>{t('editor:conflict.description', { title: documentTitle })}</p>
      </div>
      <Button variant="secondary" state={isReloading ? 'loading' : 'idle'} onClick={onReload}>
        {t('editor:conflict.reload')}
      </Button>
    </div>
  );
}
